import React, { useState, useEffect, useCallback } from 'react';
import { Todo, TodoFormData } from './types/todo';
import { todoApi } from './api/todoApi';
import TodoList from './components/TodoList';
import TodoForm from './components/TodoForm';
import './App.css';

const App: React.FC = () => {
  const [todos, setTodos] = useState<Todo[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [editingTodo, setEditingTodo] = useState<Todo | null>(null);
  const [showForm, setShowForm] = useState<boolean>(false);

  // ToDo一覧を取得
  const fetchTodos = useCallback(async () => {
    try {
      setLoading(true);
      const data = await todoApi.getAll();
      setTodos(data);
      setError(null);
    } catch (err) {
      setError('ToDoの取得に失敗しました');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchTodos();
  }, [fetchTodos]);

  // ToDo追加
  const handleAdd = async (formData: TodoFormData) => {
    try {
      const newTodo = await todoApi.create(formData);
      setTodos([...todos, newTodo]);
      setShowForm(false);
      setError(null);
    } catch (err) {
      setError('ToDoの追加に失敗しました');
      console.error(err);
    }
  };

  // ToDo更新
  const handleUpdate = async (formData: TodoFormData) => {
    if (!editingTodo) return;
    try {
      const updatedTodo = await todoApi.update(editingTodo.id, formData);
      setTodos(todos.map(todo =>
        todo.id === editingTodo.id ? updatedTodo : todo
      ));
      setEditingTodo(null);
      setShowForm(false);
      setError(null);
    } catch (err) {
      setError('ToDoの更新に失敗しました');
      console.error(err);
    }
  };

  // ToDo削除
  const handleDelete = async (id: number) => {
    if (!window.confirm('このToDoを削除しますか？')) return;
    try {
      await todoApi.delete(id);
      setTodos(todos.filter(todo => todo.id !== id));
      setError(null);
    } catch (err) {
      setError('ToDoの削除に失敗しました');
      console.error(err);
    }
  };

  // 完了/未完了の切り替え
  const handleToggleComplete = async (todo: Todo) => {
    try {
      const updatedTodo = await todoApi.update(todo.id, {
        title: todo.title,
        description: todo.description,
        due_date: todo.due_date,
        completed: !todo.completed
      });
      setTodos(todos.map(t =>
        t.id === todo.id ? updatedTodo : t
      ));
      setError(null);
    } catch (err) {
      setError('ステータスの更新に失敗しました');
      console.error(err);
    }
  };

  // 編集モード開始
  const handleEdit = (todo: Todo) => {
    setEditingTodo(todo);
    setShowForm(true);
  };

  // フォームキャンセル
  const handleCancel = () => {
    setEditingTodo(null);
    setShowForm(false);
  };

  // 新規追加ボタン
  const handleNewTodo = () => {
    setEditingTodo(null);
    setShowForm(true);
  };

  return (
    <div className="app">
      <header className="app-header">
        <h1>ToDo App</h1>
        <p className="app-subtitle">タスクを管理して生産性を向上させよう</p>
      </header>

      <main className="app-main">
        {error && (
          <div className="error-message">
            <span>{error}</span>
            <button onClick={() => setError(null)} className="error-close">×</button>
          </div>
        )}

        {!showForm && (
          <button onClick={handleNewTodo} className="btn btn-primary add-todo-btn">
            <span className="btn-icon">+</span>
            新しいToDoを追加
          </button>
        )}

        {showForm && (
          <TodoForm
            initialData={editingTodo}
            onSubmit={editingTodo ? handleUpdate : handleAdd}
            onCancel={handleCancel}
          />
        )}

        {loading ? (
          <div className="loading">
            <div className="loading-spinner"></div>
            <p>読み込み中...</p>
          </div>
        ) : (
          <TodoList
            todos={todos}
            onToggleComplete={handleToggleComplete}
            onEdit={handleEdit}
            onDelete={handleDelete}
          />
        )}
      </main>

      <footer className="app-footer">
        <p>ToDo App - React + TypeScript</p>
      </footer>
    </div>
  );
};

export default App;
